import React from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { parseEmailFrom } from "../../../../utils/formatters/parseEmailFrom";
/* Imported EXPLICITLY. Relying on a host to have imported it means the row
   renders only because CRA concatenates stylesheets globally — which is the
   state this component was extracted from: three surfaces drew this row and
   only ONE of them imported the sheet that styled it. */
import "./emailRow.css";

/**
 * EmailRow — one row in any list of emails.
 *
 * ─────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS. The app drew this row THREE times: the inbox
 * (`Emails.jsx`), the Sent page (`SentEmailsPage.jsx`) and the client
 * console's Emails panel (`ClientEmailDisplay.jsx`). All three already shared
 * ONE css recipe — so the styling was common and the markup was not, which is
 * the arrangement that guarantees drift. It had already produced two
 * divergences before this: the console had grown a sender line the inbox
 * lacked, and only the console suppressed the "att: 0" badge.
 *
 * It had also produced a silent dependency. `SentEmailsPage` imports only its
 * own stylesheet, so its rows AND its loading skeleton were styled entirely by
 * `emails.css` — a file belonging to a different page, which that page never
 * imports. It worked by bundler accident.
 *
 * ─────────────────────────────────────────────────────────────────
 * IT RENDERS THE ROW ONLY. The ACTIONS belong to the host, because they are
 * the one part that genuinely differs:
 *
 *   inbox    reply + archive in a hover rail, flags, an overflow menu
 *   console  reply + flags, both always visible (it is a narrow panel with
 *            no overflow menu, so a hover rail would strand them)
 *   sent     none at all
 *
 * Trying to express those three through props would be a switch statement
 * pretending to be an abstraction. They come in as a node.
 *
 * ─────────────────────────────────────────────────────────────────
 * `kind` PICKS WHICH ADDRESS LEADS AND WHETHER STATUS APPLIES.
 *
 *   "inbox"  leads with `from`; read/unread and flags paint the status edge.
 *   "sent"   leads with `to`; there is no unread or flag state on a sent
 *            message, so no status edge is drawn at all — an always-empty
 *            lane would just be a 4px indent nobody can explain.
 */

const EmailRow = ({
  /** the email record */
  email,
  /** "inbox" (default) | "sent" */
  kind = "inbox",
  /** () => void — opening the row */
  onOpen,
  /** the select checkbox, when the host is in select mode */
  selectSlot,
  /** the host's action rail */
  actions,
  /** render a placeholder row instead; `email` is ignored */
  loading = false,
  /** extra classes for the host (the console tags its read rows) */
  className = "",
}) => {
  if (loading) {
    return (
      <div className="email-item-wrapper">
        <div className="email-item-loading bdr-shadow-med">
          {selectSlot !== undefined && (
            /* Matches the real row's checkbox column so the list does not jump
               when the skeleton is replaced. */
            <span className="email-select-box-ghost" />
          )}
          <div className="email-item-holders-loading" />
          <div className="email-item-date-loading" />
        </div>
      </div>
    );
  }

  const isSent = kind === "sent";
  /* "Display Name <addr>" -> the two halves the row prints separately. */
  const party = parseEmailFrom(isSent ? email?.to : email?.from);

  const ms = parseInt(email?.dateCreated, 10);
  const date = ms > 0 ? BAGroupTimeFormat(ms, true) : "";

  /* No read or flag state on a sent message — `is-plain` drops the status
     lane rather than drawing an edge that can never mean anything. */
  const stateClass = isSent
    ? "is-plain"
    : `${email?.emailRead ? " read-email" : ""}${
        email?.flag === "!" ? " email-item-important" : ""
      }${email?.flag === "!!" ? " email-item-urgent" : ""}`;

  const attachments = Number(email?.attachments);

  return (
    <div className="email-item-wrapper">
      <div className={`email-item ${stateClass} ${className}`.trim()}>
        {selectSlot}
        {/*
          ONE open target. This was TWO sibling buttons on every host, both
          calling the same opener — two focus stops for one action, and the
          reason the date collided with the button cluster.
        */}
        <button
          className="email-row-open"
          onClick={onOpen}
          title={email?.emailSubject || "(no subject)"}
        >
          <span className="email-row-sender">
            <span className="email-row-name">{party.name}</span>
            {party.address && (
              <span className="email-row-addr">{party.address}</span>
            )}
          </span>
          <span className="email-row-subject">
            {email?.emailSubject ? (
              email.emailSubject
            ) : (
              /* A blank subject rendered as an empty gap and read as a broken
                 row rather than an untitled one. */
              <span className="email-row-nosubject">(no subject)</span>
            )}
          </span>
          <span className="email-row-meta">
            {/* Only when there ARE attachments. A filled green badge
                announcing "att: 0" on every row was noise on the one thing
                that should stand out when it is true. */}
            {attachments > 0 && (
              <span
                className="email-has-attach"
                title={`${attachments} attachment${
                  attachments === 1 ? "" : "s"
                }`}
              >
                <AttachFileIcon sx={{ fontSize: "13px" }} />
                {attachments}
              </span>
            )}
            <span className="email-row-date">{date || "—"}</span>
          </span>
        </button>
        {actions && <div className="email-row-actions">{actions}</div>}
      </div>
    </div>
  );
};

export default EmailRow;
